<style scoped>
.modal-dialog {
	z-index: 1060;
}
</style>

<template>
	<div
		v-if="open"
		class="modal fade show d-block"
		tabindex="-1"
		role="dialog"
		aria-modal="true"
		aria-labelledby="productRequestTitle"
		@click.self="close"
	>
		<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
			<div class="modal-content">
				<div class="modal-header">
					<h5 id="productRequestTitle" class="modal-title">
						<i class="bi bi-send me-1"></i> Request a product
					</h5>
					<button
						type="button"
						class="btn-close"
						:disabled="submitting"
						aria-label="Close"
						@click="close"
					></button>
				</div>
				<form @submit.prevent="submit">
					<div class="modal-body">
						<div class="alert alert-info small" role="note">
							The catalog is curated by Gleipnir for every Nidus district. A
							product that is not in the catalog is
							<strong>requested, never created</strong> here: a curator reviews
							your request and either promotes the product into the shared
							catalog or declines it with a reason.
						</div>

						<div v-if="error" class="alert alert-danger small" role="alert">
							{{ error }}
						</div>

						<div class="mb-3">
							<label class="form-label" for="requestProductText">
								What product do you need?
							</label>
							<textarea
								id="requestProductText"
								v-model="form.product_text"
								class="form-control"
								rows="2"
								required
								maxlength="4000"
								placeholder="e.g. Altosid XR Briquets, or the product on this label"
							></textarea>
						</div>

						<div class="mb-3">
							<label class="form-label" for="requestReason">
								Why do you need it?
							</label>
							<textarea
								id="requestReason"
								v-model="form.reason"
								class="form-control"
								rows="2"
								required
								maxlength="4000"
								placeholder="What job it does for your district, and where you use it"
							></textarea>
						</div>

						<p class="text-muted small mb-2">
							Optional: any identity you already know. A registration number
							lets the curator find the regulator's record for it; it does not
							add the product to the catalog on its own.
						</p>
						<div class="row g-2">
							<div class="col-sm-6">
								<label class="form-label small" for="requestEpa"
									>EPA registration</label
								>
								<input
									id="requestEpa"
									v-model="form.epa_reg_no"
									class="form-control form-control-sm"
									placeholder="2724-448"
								/>
							</div>
							<div class="col-sm-6">
								<label class="form-label small" for="requestCa"
									>CA registration</label
								>
								<input
									id="requestCa"
									v-model="form.ca_reg_no"
									class="form-control form-control-sm"
									placeholder="32893"
								/>
							</div>
							<div class="col-sm-6">
								<label class="form-label small" for="requestBrand">Brand</label>
								<input
									id="requestBrand"
									v-model="form.brand_name"
									class="form-control form-control-sm"
									placeholder="Altosid"
								/>
							</div>
							<div class="col-sm-6">
								<label class="form-label small" for="requestDistributor">
									Distributor
								</label>
								<input
									id="requestDistributor"
									v-model="form.distributor"
									class="form-control form-control-sm"
									placeholder="Where you buy it"
								/>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button
							type="button"
							class="btn btn-outline-secondary"
							:disabled="submitting"
							@click="close"
						>
							Cancel
						</button>
						<button
							type="submit"
							class="btn btn-primary"
							:disabled="submitting"
						>
							<span
								v-if="submitting"
								class="spinner-border spinner-border-sm me-1"
							></span>
							<i v-else class="bi bi-send me-1"></i>
							Send request
						</button>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from "vue";

import { apiClient } from "@/client";
import type { PesticideCatalogRequest } from "@/type/api";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{
	close: [];
	created: [request: PesticideCatalogRequest];
}>();

const form = reactive({
	product_text: "",
	reason: "",
	epa_reg_no: "",
	ca_reg_no: "",
	brand_name: "",
	distributor: "",
});
const submitting = ref(false);
const error = ref("");

// Each opening starts from a blank form; a request that failed is not re-sent
// by a later open.
watch(
	() => props.open,
	(open) => {
		if (!open) return;
		form.product_text = "";
		form.reason = "";
		form.epa_reg_no = "";
		form.ca_reg_no = "";
		form.brand_name = "";
		form.distributor = "";
		error.value = "";
	},
);

function close() {
	if (submitting.value) return;
	emit("close");
}

async function submit() {
	error.value = "";
	submitting.value = true;
	try {
		const created = await apiClient.JSONPost<PesticideCatalogRequest>(
			"/api/pesticide-catalog/requests",
			{
				product_text: form.product_text.trim(),
				reason: form.reason.trim(),
				epa_reg_no: form.epa_reg_no.trim() || undefined,
				ca_reg_no: form.ca_reg_no.trim() || undefined,
				brand_name: form.brand_name.trim() || undefined,
				distributor: form.distributor.trim() || undefined,
			},
		);
		emit("created", created);
		emit("close");
	} catch (e: any) {
		error.value = e?.message || "Failed to send the request.";
	} finally {
		submitting.value = false;
	}
}
</script>
